import type { CaseStage, FamilyStep, FamilyStepKey } from '../../api/types';
import type { DictKey } from '../../i18n';
import type { StageMark } from './StageProgress';

/** The eight steps the family sees, in order (contract of batch p5-A). */
export const STEP_KEYS: readonly FamilyStepKey[] = [
  'accepted',
  'docs_collecting',
  'docs_ready',
  'picked_up',
  'in_transit',
  'border_crossed',
  'delivered',
  'burial',
];

export function isStepKey(k: string): k is FamilyStepKey {
  return (STEP_KEYS as readonly string[]).includes(k);
}

/** Known steps only (an unknown key from a newer backend is skipped rather than shown raw), in the API order. */
export function knownSteps(items: FamilyStep[] | null | undefined): FamilyStep[] {
  return (items ?? []).filter((i) => i && typeof i.key === 'string' && isStepKey(i.key));
}

/**
 * Where the family is now = the last reached step (it gets «Зараз» and the hint); earlier reached steps are done,
 * the rest are next. The API's `current` means «the next step to reach» — used only when nothing is reached yet.
 * All steps reached → all done, no hint.
 */
export function stepMarks(items: FamilyStep[]): StageMark[] {
  const reached = (s: FamilyStep) => s.done || !!s.reached_at;
  let last = -1;
  items.forEach((s, i) => {
    if (reached(s)) last = i;
  });
  if (last === items.length - 1 && last >= 0) return items.map(() => 'done');
  if (last < 0) {
    const cur = Math.max(0, items.findIndex((s) => s.current));
    return items.map((_, i) => (i === cur ? 'current' : 'next'));
  }
  return items.map((s, i) => (i === last ? 'current' : i < last || reached(s) ? 'done' : 'next'));
}

export function stepLabelKey(key: FamilyStepKey): DictKey {
  return `step.${key}` as DictKey;
}

/**
 * One calm line under the current step. «У дорозі» promises a word about the border only when the route crosses one
 * (border_crossed is among the steps); otherwise only about the arrival.
 */
export function stepHintKey(key: FamilyStepKey, items: FamilyStep[]): DictKey {
  if (key === 'in_transit' && !items.some((i) => i.key === 'border_crossed')) return 'step.hint.in_transit_local';
  return `step.hint.${key}` as DictKey;
}

/** Steps as stages, for the compact progress line on the cases list. */
export function stepsAsStages(items: FamilyStep[], label: (k: FamilyStepKey) => string): CaseStage[] {
  return knownSteps(items).map((s) => ({ key: s.key, name: label(s.key as FamilyStepKey), done: s.done, current: s.current }));
}
